/**
 * <ModuleRouteGuard> — the per-user module entitlement gate for routes.
 *
 * Sits inside <AccessGate> around the whole shell route tree. For an
 * unrestricted user (allowed_modules NULL) or an app admin it renders children
 * untouched. For a restricted user it:
 *   1. default-denies any path not owned by an allowed module (see
 *      ROUTE_MODULES in config/moduleEntitlements) and redirects to the first
 *      allowed module's home,
 *   2. on the first visit to "/" after sign-in, lands them on that home instead
 *      of the tile grid (later visits to "/" show the filtered tiles),
 *   3. keeps the management context on an allowed module, so the sidebar never
 *      renders another module's navigation.
 *
 * Public routes (/f, /i, /s, /b, /test-portal) render before the auth wall in
 * App.tsx and never reach this guard.
 */

import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAppStore } from '@/store/appStore'
import { useAuthStore } from '@/store/authStore'
import { MODULE_REGISTRY } from '@/config/tesseractConfig'
import {
  useModuleEntitlement,
  useModuleEntitlementStore,
  isRestricted,
  resolveModuleRedirect,
  firstAllowedModule,
  modulesForPath,
  moduleForContext,
  isModuleVisibleFor,
  type ModuleEntitlement,
} from '@/config/moduleEntitlements'
import type { ManagementContext } from '@/store/appStore'

/** Context the sidebar should show for a restricted user on `pathname`. */
function contextFor(pathname: string, e: ModuleEntitlement): ManagementContext | null {
  const owners = modulesForPath(pathname)
  const key =
    (owners !== 'always' && owners.find((k) => isModuleVisibleFor(k, e))) || firstAllowedModule(e)
  const def = key ? MODULE_REGISTRY.find((m) => m.key === key) : undefined
  return def?.contexts[0] ?? null
}

export function ModuleRouteGuard({ children }: { children: React.ReactNode }) {
  const entitlement = useModuleEntitlement()
  const restricted = isRestricted(entitlement)
  const { pathname } = useLocation()
  const identity = useAuthStore((s) => s.user?.email ?? null)
  const landedFor = useModuleEntitlementStore((s) => s.landedFor)
  const markLanded = useModuleEntitlementStore((s) => s.markLanded)
  const managementContext = useAppStore((s) => s.managementContext)
  const setManagementContext = useAppStore((s) => s.setManagementContext)

  const landing = restricted && !!identity && landedFor !== identity
  const target = resolveModuleRedirect(pathname, entitlement, { landing })

  // The landing redirect runs once per sign-in; any first navigation counts.
  useEffect(() => {
    if (restricted && identity && landedFor !== identity) markLanded(identity)
  }, [restricted, identity, landedFor, markLanded])

  // Keep the sidebar on an allowed module (a persisted context from an earlier
  // unrestricted session, or a cross-module link, could point elsewhere).
  useEffect(() => {
    if (!restricted || target) return
    const ctxModule = managementContext ? moduleForContext(managementContext) : undefined
    if (pathname === '/') {
      // Home: drop a context (e.g. the portfolio picker) this user cannot open.
      if (managementContext && !(ctxModule && isModuleVisibleFor(ctxModule, entitlement))) {
        setManagementContext(null)
      }
      return
    }
    const owners = modulesForPath(pathname)
    const ctxFits =
      !!ctxModule &&
      isModuleVisibleFor(ctxModule, entitlement) &&
      (owners === 'always' || owners.includes(ctxModule))
    if (!ctxFits) {
      const next = contextFor(pathname, entitlement)
      if (next && next !== managementContext) setManagementContext(next)
    }
  }, [restricted, target, pathname, managementContext, setManagementContext, entitlement])

  if (target && target !== pathname) return <Navigate to={target} replace />
  return <>{children}</>
}
